import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { Loader2, Briefcase, TrendingUp, AlertTriangle, Wallet, CheckCircle2, Users, Percent, FileText, Layers, Building2 } from "lucide-react";

function Kpi({ label, value, sub, icon: Icon, accent, testid, suffix }) {
  return (
    <div className="fc-card p-5 fc-card-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${accent || "border-[#262626] text-[#A3A3A3]"}`}>
            <Icon size={15} strokeWidth={1.8}/>
          </div>
        )}
      </div>
      <div className="fc-kpi-value text-2xl sm:text-[26px] mt-3 leading-none">
        {value}{suffix && <span className="text-[14px] text-[#737373] ml-1">{suffix}</span>}
      </div>
      {sub && <div className="text-[11px] text-[#737373] mt-2 leading-snug">{sub}</div>}
    </div>
  );
}

export default function ExecutiveDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/executive");
        setData(data);
      } catch (_) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="mb-7">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Visão estratégica</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Dashboard Executivo</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Consolidado: contratos normais + parcelados + acordos</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map(i=><div key={i} className="fc-card h-32 shimmer"/>)}</div>
      ) : !data ? (
        <div className="fc-card p-10 text-center text-[12.5px] text-[#737373]">Erro ao carregar dados.</div>
      ) : (
        <>
          {/* === Capital Próprio (destaque) === */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-400/80 mb-2">Capital Próprio da Empresa</div>
          <div
            className="fc-card p-5 sm:p-6 mb-6 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(20,83,45,0.06) 50%, rgba(0,0,0,0) 100%)",
              borderColor: "rgba(16,185,129,0.35)",
            }}
            data-testid="exec-capital-proprio-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center border border-emerald-500/50 text-emerald-300 bg-emerald-500/10">
                    <Building2 size={17} strokeWidth={1.8}/>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-emerald-400/80 font-medium">Capital Próprio exposto</div>
                    <div className="text-[10.5px] text-[#737373]">Operações 100% empresa — sem split com sócios</div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="fc-kpi-value text-[28px] sm:text-[32px] leading-none text-emerald-200" data-testid="exec-capital-proprio-total">
                  {formatBRL(data.capital_proprio_total_exposto || 0)}
                </div>
                <div className="text-[11px] text-[#A3A3A3] mt-1">
                  Contratos: {formatBRL(data.capital_proprio_contratos || 0)} · Parc/Acordos: {formatBRL(data.capital_proprio_parcelados || 0)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-black/30 rounded-md p-3 border border-emerald-500/15" data-testid="exec-capital-proprio-recuperado">
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Capital recuperado</div>
                <div className="font-mono-tab text-[15px] text-emerald-300 mt-1">{formatBRL(data.capital_proprio_recuperado || 0)}</div>
              </div>
              <div className="bg-black/30 rounded-md p-3 border border-emerald-500/15" data-testid="exec-capital-proprio-lucro-recebido">
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Lucro recebido (100%)</div>
                <div className="font-mono-tab text-[15px] text-emerald-300 mt-1">{formatBRL(data.lucro_capital_proprio_recebido || 0)}</div>
              </div>
              <div className="bg-black/30 rounded-md p-3 border border-emerald-500/15" data-testid="exec-capital-proprio-lucro-pendente">
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Lucro pendente</div>
                <div className="font-mono-tab text-[15px] text-amber-300 mt-1">{formatBRL(data.lucro_capital_proprio_pendente || 0)}</div>
              </div>
              <div className="bg-black/30 rounded-md p-3 border border-emerald-500/15" data-testid="exec-capital-proprio-operacoes">
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Operações ativas</div>
                <div className="font-mono-tab text-[15px] text-emerald-200 mt-1">
                  {(data.contratos_proprios_ativos || 0) + (data.parcelados_proprios_ativos || 0)}
                  <span className="text-[11px] text-[#737373] ml-1">
                    ({data.contratos_proprios_ativos || 0} contr. + {data.parcelados_proprios_ativos || 0} parc.)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Capital */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mb-2">Capital na rua — Carteira Ativa × Carteira em Recuperação</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Capital total exposto" value={formatBRL(data.capital_total_exposto)}
              sub={`Ativo: ${formatBRL(data.capital_ativo_total)} · Recuperação: ${formatBRL(data.capital_recuperacao_total)}`}
              icon={Wallet} accent="border-[#D4AF37]/40 text-[#D4AF37]" testid="exec-capital-exposto"/>
            <Kpi label="Carteira Ativa" value={formatBRL(data.capital_ativo_total)}
              sub={`Contratos: ${formatBRL(data.capital_contratos_ativo)} · Parc/Acordos: ${formatBRL(data.capital_parcelados_ativo)}`}
              icon={Wallet} accent="border-emerald-500/40 text-emerald-300" testid="exec-carteira-ativa"/>
            <Kpi label="Carteira em Recuperação" value={formatBRL(data.capital_recuperacao_total)}
              sub={`${(data.qtd_contratos_recuperacao || 0) + (data.qtd_parcelados_recuperacao || 0)} operações · Contratos: ${data.qtd_contratos_recuperacao || 0} · Parc: ${data.qtd_parcelados_recuperacao || 0}`}
              icon={AlertTriangle} accent="border-amber-500/40 text-amber-300" testid="exec-carteira-recuperacao"/>
            <Kpi label="Capital recuperado (histórico)" value={formatBRL(data.capital_recuperado_total)}
              sub={`Contratos: ${formatBRL(data.capital_recuperado_contratos)} · Parc/Acordos: ${formatBRL(data.capital_recuperado_parcelados)}`}
              icon={CheckCircle2} accent="border-emerald-500/40 text-emerald-300" testid="exec-cap-recuperado"/>
          </div>

          {/* Inadimplência (operacional — não conta recuperação) */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mb-2">Inadimplência operacional (excl. recuperação)</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Inadimplência total" value={formatBRL(data.inadimplencia_total)}
              sub={`Taxa: ${data.taxa_inadimplencia.toFixed(2)}%`}
              icon={AlertTriangle} accent="border-red-500/40 text-red-300" testid="exec-inadimplencia"/>
            <Kpi label="Taxa inadimplência" value={`${data.taxa_inadimplencia.toFixed(2)}%`}
              icon={Percent} accent="border-yellow-500/40 text-yellow-300" testid="exec-taxa-inad"/>
            <Kpi label="Contratos em atraso" value={data.contratos_atrasados} icon={FileText} accent="border-red-500/30 text-red-300" testid="exec-contratos-atrasados"/>
            <Kpi label="Parcelados em atraso" value={data.parcelados_atrasados} icon={Layers} accent="border-red-500/30 text-red-300" testid="exec-parcelados-atrasados"/>
          </div>

          {/* Lucros */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mb-2">Lucros</div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Kpi label="Lucro recebido (consolidado)" value={formatBRL(data.lucro_recebido_total)}
              sub={`Contratos: ${formatBRL(data.lucro_recebido_contratos)} · Parc/Acordos: ${formatBRL(data.lucro_recebido_parcelados)}`}
              icon={TrendingUp} accent="border-emerald-500/40 text-emerald-300" testid="exec-lucro-recebido"/>
            <Kpi label="Lucro pendente" value={formatBRL(data.lucro_pendente_total)}
              sub="Soma de juros previstos em aberto + parcelas futuras"
              icon={Briefcase} accent="border-amber-500/40 text-amber-300" testid="exec-lucro-pendente"/>
          </div>

          {/* Carteira */}
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mb-2">Carteira</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Clientes ativos" value={data.clientes_ativos_totais} icon={Users} testid="exec-clientes-ativos"/>
            <Kpi label="Clientes inadimplentes" value={data.clientes_inadimplentes} icon={Users} accent="border-red-500/30 text-red-300" testid="exec-clientes-inad"/>
            <Kpi label="Contratos ativos" value={data.contratos_ativos} icon={FileText} testid="exec-contratos-ativos"/>
            <Kpi label="Parcelados/Acordos ativos" value={data.parcelados_ativos} icon={Layers} testid="exec-parcelados-ativos"/>
          </div>
        </>
      )}
    </Layout>
  );
}
